import { cn } from '@kalos/ui';
import { cloneElement, type CSSProperties, type ReactElement, useEffect } from 'react';

import { flagColor } from './colors';
import { IS_PROD } from './evaluate';
import { registerFlagRead } from './reads';
import { type FlagKey } from './registry';

type Markable = ReactElement<{ className?: string; style?: CSSProperties }>;

/**
 * Marks the UI a feature flag owns. Wrap the one element the flag turns on, where it renders;
 * the gate stays where it is (useFeatureFlag or a ternary).
 *
 * Outside prod it draws a dashed border in the flag's colour and a small pill naming it on the
 * element itself (index.css, `[data-feature-flag]`): no wrapper box, so the layout QA sees is
 * the layout prod renders. It also registers the region so the legend lists the flag. In prod
 * it renders the element untouched.
 *
 *   {showFollowUp && (
 *     <Flagged flag="job-details.follow-up-flow">
 *       <Button onClick={openFollowUp}>Follow up</Button>
 *     </Flagged>
 *   )}
 */
export function Flagged({ flag, children }: { flag: FlagKey; children: Markable }) {
  useEffect(() => (IS_PROD ? undefined : registerFlagRead(flag, true, 'region')), [flag]);
  if (IS_PROD) return children;
  return cloneElement(children, {
    className: cn(children.props.className, 'relative'),
    style: { ...children.props.style, '--feature-flag-color': flagColor(flag) } as CSSProperties,
    'data-feature-flag': `Feature flag: ${flag}`,
  } as Partial<Markable['props']>);
}
